import posthog from 'posthog-js';

import type { HeroVariant } from '~/marketing/presenceContract';

export const FLAG_HERO = 'experiment-landing-hero-presence-v1';
export const FLAG_KILL_API = 'kill-switch-presence-api';
export const FLAG_KILL_MOTION = 'kill-switch-landing-motion';

let initialized = false;

function getKey(): string | undefined {
  return import.meta.env.VITE_POSTHOG_KEY?.trim() || undefined;
}

export function isPosthogEnabled(): boolean {
  return Boolean(getKey());
}

function getHost(): string {
  return import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com';
}

/**
 * One-time PostHog init. Pass stable anon id for experiments + funnel analysis.
 */
export function initPosthogOnce(distinctId: string): void {
  if (initialized) {
    if (getKey()) posthog.identify(distinctId);
    return;
  }
  const key = getKey();
  if (!key) {
    if (import.meta.env.DEV) {
      console.info('[presence] VITE_POSTHOG_KEY not set; PostHog disabled.');
    }
    return;
  }
  posthog.init(key, {
    api_host: getHost(),
    person_profiles: 'identified_only',
    capture_pageview: true,
    persistence: 'localStorage+cookie',
    loaded: (ph) => {
      ph.identify(distinctId);
    },
  });
  initialized = true;
}

/**
 * Re-identify (e.g. if anon id became available after first init).
 */
export function ensurePosthogIdentify(distinctId: string) {
  if (!getKey() || !initialized) return;
  posthog.identify(distinctId);
}

export function onPosthogFlagsLoaded(cb: () => void): (() => void) | void {
  if (!getKey()) {
    queueMicrotask(cb);
    return;
  }
  if (!initialized) {
    queueMicrotask(cb);
    return;
  }
  return posthog.onFeatureFlags?.(cb) as (() => void) | void;
}

function normalizeVariant(raw: unknown): HeroVariant {
  if (raw === 'A' || raw === 'B' || raw === 'C' || raw === 'control') return raw;
  if (typeof raw === 'string') {
    const u = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    if (u === 'A' || u === 'B' || u === 'C' || u === 'Control') {
      if (u === 'Control') return 'control';
      return u as HeroVariant;
    }
  }
  if (raw === true) return 'A';
  return 'control';
}

export function getResolvedHeroVariant(): HeroVariant {
  if (!getKey() || !initialized) return 'control';
  const v = posthog.getFeatureFlag(FLAG_HERO);
  return normalizeVariant(v);
}

export function isPresenceApiKilled(): boolean {
  if (!getKey() || !initialized) return false;
  return posthog.isFeatureEnabled(FLAG_KILL_API) === true;
}

export function isLandingMotionKilled(): boolean {
  if (!getKey() || !initialized) return false;
  return posthog.isFeatureEnabled(FLAG_KILL_MOTION) === true;
}

export const presenceEvents = {
  landing_page_view: 'landing_page_view',
  hero_variant_assigned: 'hero_variant_assigned',
  hero_variant_viewed: 'hero_variant_viewed',
  hero_primary_cta_click: 'hero_primary_cta_click',
  hero_secondary_cta_click: 'hero_secondary_cta_click',
  // Three events, not one. The submit event fired only after a successful response, so a backend
  // outage or a validation wall produced silence that was indistinguishable from nobody trying —
  // on the single conversion this page exists for.
  /** The page had no share affordance at all until this event existed to measure one. */
  share_clicked: 'share_clicked',
  waitlist_full_release_attempt: 'waitlist_full_release_attempt',
  waitlist_full_release_submit: 'waitlist_full_release_submit',
  waitlist_full_release_error: 'waitlist_full_release_error',
} as const;

export function captureEvent(
  event: (typeof presenceEvents)[keyof typeof presenceEvents],
  props: Record<string, string | number | boolean | undefined>
) {
  if (!getKey() || !initialized) return;
  const cleaned: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v !== undefined) cleaned[k] = v;
  }
  posthog.capture(event, cleaned);
}

export { posthog };
